import { Chip, Icon, makeStyles, Paper, Tooltip } from "@material-ui/core";
import { Typography } from "@material-ui/core";
import { TextField, Button } from "@material-ui/core";
import clsx from "clsx";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import DictionaryAutocomplete, { OptionType } from "../../common/controls/DictionaryAutocomplete";
import ResourcesAutocompleteMI, { OptionType as ResourceOption } from "../../common/controls/ResourcesAutocompleteMI";

const useStyles = makeStyles((theme) => ({
	ordersRowControls: {
		display: 'flex',
		marginBottom: '14px',
	},
	ordersDictionaryControl: {
		flex: '1',
		marginRight: '14px',
	},
	ordersQuantityControl: {
		width: '120px'
	},
	ordersDescriptionControl: {
		marginBottom: '14px',
	},
	rootChips: {
		display: 'flex',
		flexWrap: 'wrap',
		listStyle: 'none',
		padding: theme.spacing(0.5),
		margin: 0,
	},
	isNotValidRoot: {
		border: "1px solid red",
	},
	chip: {
		margin: theme.spacing(0.5),
	},
}));

interface IOrderItem {
	quantity: string;
	description: string;

	positionId: string;
	productId: string;
	resourceId: string;

	name: string
}

interface IProps {
	refId: string;
	typeRefId: string;
	isValid: boolean;
	isLogSystemIntegrationAvailable: boolean;
	variant: 'LogOrder' | 'Order';

	filterId: string;
}

function Orders(props: IProps) {
	const { t } = useTranslation();
	const classes = useStyles();

	const [typeId, setTypeId] = React.useState("");

	const [quantity, setQuantity] = React.useState("");
	const [description, setDescription] = React.useState("");

	const [product, setProduct] = React.useState<OptionType>(null);
	const [position, setPosition] = React.useState<OptionType>(null);

	const [resource, setResource] = React.useState<ResourceOption[]>([]);

	
	const [positionError, setPositionError] = React.useState("");
	const [quantityError, setQuantityError] = React.useState("");
	const [items, setItems] = React.useState<IOrderItem[]>([]);

	React.useEffect(() => {
		const inputElement = document.getElementById(props.refId) as HTMLInputElement;
		if (!inputElement) {
			console.warn('Not Found "' + props.refId+ '"');
		}
		else if (inputElement.value) {
			setItems(JSON.parse(inputElement.value));
		}

		const typeElement = document.getElementById(props.typeRefId) as HTMLInputElement;
		if (!typeElement) {
			console.warn('Not Found "' + props.typeRefId + '"');
		}
		else if (typeElement.value) {
			setTypeId(typeElement.value);
		}
	}, []);

	const handleDelete = (chipToDelete: IOrderItem, i) => {
		const copy = [...items];
		copy.splice(i, 1);
		setItems(copy);

		updateField(copy);
	};

	const handleAdd = () => {
		if (!isValid()) {
			return;
		}
		const copy = [...items];
		copy.push({
			description: description,
			name: position?.label ?? resource[0]?.label,
			positionId: position?.value,
			quantity: quantity,
			productId: product?.value,
			resourceId: resource[0]?.value,
		});
		setItems(copy);
		setDescription("");
		setQuantity("");
		setProduct(null);
		setPosition(null);
		setResource([]);

		updateField(copy);
	};

	function isValid() {

		var result = true;
		let qError = "";
		let pError = "";

		if (props.variant === 'Order') {
			if (!position) {
				pError = t("common.message.fieldIsRequired");
				result = false;
			}
		}
			
		if (props.variant === 'LogOrder') {
			if (resource.length === 0 ) {
				pError = t("common.message.fieldIsRequired");
				result = false;
			}
		}

		if (!quantity || quantity == '0') {
			qError = t("common.message.fieldIsRequired");
			result = false;
		}
		var quantityNumber = parseInt(quantity, 10);
		if (isNaN(quantityNumber) || quantityNumber === 0) {
			qError = t("common.message.fieldIsRequired");
			result = false;
		}
		setPositionError(pError);
		setQuantityError(qError);
		return result;
	}

	function updateField(newFields: IOrderItem[]) {
		const inputElement = document.getElementById(props.refId) as HTMLInputElement;
		if (!inputElement) {
			console.warn('Not Found "' + props.refId + '"');
			return;
		}
		if (newFields.length === 0) {
			inputElement.value = "";
		} else {
			inputElement.value = JSON.stringify(newFields);
		}
	}

	return (<div className="layout-grid">
		<input type="hidden" value={product?.value ?? ""} id={props.refId + "_prod"} />
		<div>
			<div className={classes.ordersRowControls}>
				{props.variant === 'Order' && <>
					<DictionaryAutocomplete
						className={classes.ordersDictionaryControl}
						label={t("control.orders.orderProduct")}
						parentDictionaryId={typeId}
						dictionaryName="OrderProduct"
						value={product}
						onChange={(e) => {
							setProduct(e);
							setPosition(null);
						}}
					/>
					<DictionaryAutocomplete
						disabled={!product?.value}
						className={classes.ordersDictionaryControl}
						label={t("control.orders.orderPosition")}
						dictionaryName="OrderPosition"
						parentDictionaryId={product?.value}

						value={position}
						onChange={(e) => setPosition(e)}
						helperText={positionError}
						error={!!positionError}
					/>
				</>}
				{props.variant === 'LogOrder' && <div className={classes.ordersDictionaryControl}>
					<ResourcesAutocompleteMI
						value={resource}
						onSelect={(v) => setResource(v)}
						multiple={false}
						filters={{
							maxRows: 40,
							filterId: props.filterId,
							loadOnFocus: false,
						}}
						placeholderText={t("control.orders.asset")}
						grid={null}
					/>
					<Typography color="error" variant="caption">{positionError}</Typography>
				</div>}
				<TextField
					className={classes.ordersQuantityControl}
					label={t("control.orders.quantity")}
					variant="outlined"
					value={quantity}
					onChange={(e) => setQuantity(e.target.value)}
					helperText={quantityError}
					error={!!quantityError}
				/>
			</div>
			<TextField
				className={classes.ordersDescriptionControl}
				fullWidth
				multiline
				label={t("control.orders.description")}
				rows={2}
				variant="outlined"
				value={description}
				onChange={(e) => setDescription(e.target.value)}
			/>
			<Button
				startIcon={<Icon>add</Icon>}
				type="button"
				onClick={() => handleAdd()}
			>
				{t("common.action.add")}
			</Button>
		</div>
		<ul className={clsx(classes.rootChips, {
			[classes.isNotValidRoot]: !props.isValid
		})}>
			{items.map((data, i) => {
				let icon;

				if (data.description) {
					icon = (<Tooltip title={data.description}>
						<Icon>description</Icon>
					</Tooltip>);
				}

				return (
					<li key={i}>
						<Chip
							icon={icon}
							label={data.name + ": " + data.quantity + " " + t("control.orders.quantityUnit")}
							onDelete={() => handleDelete(data, i)}
							className={classes.chip}
						/>
					</li>
				);
			})}
		</ul>
	</div>);
}

export default {
	init: (id: string) => {
		const rootElement: HTMLElement = document.getElementById(id);

		if (!rootElement) {
			return;
		}
		return ReactDOM.render(
			<React.Suspense fallback="loading">
				<Orders
					variant={rootElement.dataset.variant as any}
					filterId={rootElement.dataset.filterId}
					refId={rootElement.dataset.refId}
					typeRefId={rootElement.dataset.typeRefId}
					isValid={rootElement.dataset.isValid === 'True'}
					isLogSystemIntegrationAvailable={rootElement.dataset.isLogSystemIntegrationAvailable === 'True'}
				/>
			</React.Suspense>,
			rootElement);
	}
}